import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const LeaveManagement = () => {
  const [searchParams] = useSearchParams();
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 25,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    status: '',
    leaveType: '',
    employeeId: 'all'
  });
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [resetYear, setResetYear] = useState(new Date().getFullYear());
  const [actionLoading, setActionLoading] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [selectedEmployeeName, setSelectedEmployeeName] = useState('');
  const searchTimeoutRef = useRef(null);

  // Check for leaveId in URL query params
  useEffect(() => {
    const leaveIdFromUrl = searchParams.get('leaveId');
    if (leaveIdFromUrl) {
      // If leaveId is in URL, we can highlight it or scroll to it after data loads
      // For now, just fetch leaves normally
      console.log('Leave ID from URL:', leaveIdFromUrl);
    }
  }, [searchParams]);

  // Debounce search: wait 500ms after user stops typing before triggering API
  useEffect(() => {
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout - only trigger API if search is empty or has at least 3 characters
    if (search === '' || search.trim().length >= 3) {
      searchTimeoutRef.current = setTimeout(() => {
        setDebouncedSearch(search);
      }, 500); // 500ms delay after user stops typing
    } else {
      // If less than 3 characters, clear the debounced search
      setDebouncedSearch('');
    }

    // Cleanup timeout on unmount or when search changes
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search]);

  // Reset to page 1 when debounced search changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [debouncedSearch]);

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLeaves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.limit, debouncedSearch, sortBy, sortOrder, filters]);

  const fetchEmployees = async () => {
    if (!API_URL) return;

    try {
      const currentUserId = localStorage.getItem('id');
      
      if (!currentUserId) {
        return;
      }

      const payload = {
        action: 'get-employees',
        page: 1,
        limit: 1000
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        const employeeList = result.employees || [];
        setEmployees(employeeList);
      }
    } catch (error) {
      // Silently fail - employees dropdown is optional
      console.error('Error fetching employees:', error);
    }
  };

  const fetchLeaves = async () => {
    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    setLoading(true);
    try {
      const currentUserId = localStorage.getItem('id');
      
      if (!currentUserId) {
        toast.error('User ID not found. Please login again.');
        return;
      }

      const payload = {
        action: 'get-all-leaves',
        currentUserId: currentUserId,
        page: pagination.currentPage,
        limit: pagination.limit,
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(filters.year && { year: filters.year }),
        ...(filters.status && { status: filters.status }),
        ...(filters.leaveType && { leaveType: filters.leaveType }),
        ...(filters.employeeId && filters.employeeId !== 'all' && { employeeId: filters.employeeId }),
        ...(sortBy && { sortBy: sortBy, sortOrder: sortOrder })
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        setLeaves(result.data || []);
        setPagination(prev => ({
          ...prev,
          currentPage: result.page || prev.currentPage,
          totalPages: result.totalPages || prev.totalPages,
          totalCount: result.total || prev.totalCount,
          hasNextPage: (result.page || prev.currentPage) < (result.totalPages || prev.totalPages),
          hasPreviousPage: (result.page || prev.currentPage) > 1
        }));
      } else {
        toast.error(result?.message || 'Failed to fetch leaves');
      }
    } catch (error) {
      console.error('Error fetching leaves:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const day = date.getDate().toString().padStart(2, '0');
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      }
      return dateString;
    } catch (error) {
      return dateString;
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setPagination(prev => ({ ...prev, limit: newLimit, currentPage: 1 }));
  };

  const getPaginationItems = () => {
    const totalPages = pagination.totalPages;
    const currentPage = pagination.currentPage;

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const items = [];
    const addPage = (page) => {
      if (!items.includes(page)) {
        items.push(page);
      }
    };
    const addEllipsis = () => {
      if (items[items.length - 1] !== 'ellipsis') {
        items.push('ellipsis');
      }
    };

    if (currentPage <= 3) {
      for (let page = 1; page <= Math.min(5, totalPages); page += 1) {
        addPage(page);
      }
      if (totalPages > 8) {
        addEllipsis();
      }
      for (let page = Math.max(totalPages - 2, 1); page <= totalPages; page += 1) {
        addPage(page);
      }
    } else if (currentPage >= totalPages - 2) {
      for (let page = 1; page <= Math.min(3, totalPages); page += 1) {
        addPage(page);
      }
      if (totalPages > 8) {
        addEllipsis();
      }
      for (let page = Math.max(totalPages - 4, 1); page <= totalPages; page += 1) {
        addPage(page);
      }
    } else {
      for (let page = Math.max(currentPage - 2, 1); page <= Math.min(currentPage + 2, totalPages); page += 1) {
        addPage(page);
      }
      addEllipsis();
      for (let page = Math.max(totalPages - 3, 1); page <= totalPages; page += 1) {
        addPage(page);
      }
    }

    return items;
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value); // Update input immediately for good UX
    // Debouncing and pagination reset is handled in useEffect
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const getSerialNumber = (index) => {
    return (pagination.currentPage - 1) * pagination.limit + index + 1;
  };

  const handleApprove = (leave) => {
    setSelectedLeave(leave);
    setShowApproveModal(true);
  };

  const handleReject = (leave) => {
    setSelectedLeave(leave);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const closeApproveModal = () => {
    setShowApproveModal(false);
    setSelectedLeave(null);
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setSelectedLeave(null);
    setRejectionReason('');
  };

  const confirmApprove = async () => {
    if (!selectedLeave) return;

    const currentUserId = localStorage.getItem('id');
    
    if (!currentUserId) {
      toast.error('User ID not found. Please login again.');
      closeApproveModal();
      return;
    }

    setLoading(true);
    try {
      const payload = {
        action: 'approve-leave',
        leaveId: selectedLeave.id,
        currentUserId: currentUserId
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result?.message || 'Leave approved successfully');
        closeApproveModal();
        fetchLeaves();
      } else {
        toast.error(result?.message || 'Failed to approve leave');
      }
    } catch (error) {
      console.error('Error approving leave:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const confirmReject = async () => {
    if (!selectedLeave) return;

    const currentUserId = localStorage.getItem('id');
    
    if (!currentUserId) {
      toast.error('User ID not found. Please login again.');
      closeRejectModal();
      return;
    }

    setLoading(true);
    try {
      const payload = {
        action: 'reject-leave',
        leaveId: selectedLeave.id,
        currentUserId: currentUserId,
        ...(rejectionReason && { rejectionReason: rejectionReason.trim() })
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result?.message || 'Leave rejected successfully');
        closeRejectModal();
        fetchLeaves();
      } else {
        toast.error(result?.message || 'Failed to reject leave');
      }
    } catch (error) {
      console.error('Error rejecting leave:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase();
    let className = 'employee-status';
    let text = status?.toUpperCase() || 'PENDING';

    switch (statusLower) {
      case 'approved':
        className += ' employee-status-active';
        break;
      case 'rejected':
        className += ' employee-status-inactive';
        break;
      case 'cancelled':
        className += ' employee-status-inactive';
        break;
      default:
        className += ' employee-status-active';
        text = 'PENDING';
    }

    return <span className={className}>{text}</span>;
  };

  const getLeaveTypeLabel = (type) => {
    const types = {
      'casual': 'Casual',
      'sick': 'Sick',
      'earned': 'Earned',
      'lop': 'LOP',
      'permission': 'Permission',
      'Travel': 'Travel'
    };
    return types[type] || type?.toUpperCase() || '-';
  };

  const truncateText = (text, maxLength = 30) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleViewReason = (reason, employeeName) => {
    setSelectedReason(reason || '-');
    setSelectedEmployeeName(employeeName || 'Employee');
    setShowReasonModal(true);
  };

  const closeReasonModal = () => {
    setShowReasonModal(false);
    setSelectedReason('');
    setSelectedEmployeeName('');
  };

  const handleResetYearly = () => {
    setResetYear(new Date().getFullYear());
    setShowResetModal(true);
  };

  const handleAllocateMonthly = () => {
    setShowAllocateModal(true);
  };

  const closeResetModal = () => {
    setShowResetModal(false);
    setResetYear(new Date().getFullYear());
  };

  const closeAllocateModal = () => {
    setShowAllocateModal(false);
  };

  const confirmResetYearly = async () => {
    const currentUserId = localStorage.getItem('id');
    
    if (!currentUserId) {
      toast.error('User ID not found. Please login again.');
      closeResetModal();
      return;
    }

    setActionLoading(true);
    try {
      const payload = {
        action: 'reset-yearly-leaves',
        currentUserId: currentUserId,
        year: resetYear
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result?.message || `Yearly leave reset completed for year ${resetYear}`);
        closeResetModal();
      } else {
        toast.error(result?.message || 'Failed to reset yearly leaves');
      }
    } catch (error) {
      console.error('Error resetting yearly leaves:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const confirmAllocateMonthly = async () => {
    const currentUserId = localStorage.getItem('id');
    
    if (!currentUserId) {
      toast.error('User ID not found. Please login again.');
      closeAllocateModal();
      return;
    }

    setActionLoading(true);
    try {
      const payload = {
        action: 'allocate-monthly-leaves',
        currentUserId: currentUserId
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result?.message || `Monthly leave allocation completed. Allocated to ${result.allocated || 0} employees.`);
        closeAllocateModal();
      } else {
        toast.error(result?.message || 'Failed to allocate monthly leaves');
      }
    } catch (error) {
      console.error('Error allocating monthly leaves:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const isReportingManager = localStorage.getItem('isReportingManager') === 'true';
  
  // Check if user is admin by designation
  const designationName = localStorage.getItem('designationName') || '';
  const normalizedDesignation = designationName.replace(/\u00A0/g, ' ').trim();
  const adminDesignation = process.env.REACT_APP_ADMIN_DESIGNATION || '';
  const isAdminByDesignation = adminDesignation && normalizedDesignation.toLowerCase() === adminDesignation.trim().toLowerCase();
  
  // Show buttons for admins or non-reporting managers
  const showAdminButtons = isAdminByDesignation || !isReportingManager;

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          {/* Header with Title */}
          <div className="row mb-3 employee-header-row">
            <div className="col-md-8">
              <h1 className="employee-title">
                Approve Leaves/Travels
                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                View, approve, and manage employee leave applications.
              </p>
            </div>
            {showAdminButtons && (
              <div className="col-md-4" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', alignItems: 'flex-start', marginTop: '10px' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAllocateMonthly}
                  disabled={actionLoading}
                  style={{ padding: '8px 16px', fontSize: '14px', backgroundColor: '#10b981', borderColor: '#10b981' }}
                >
                  <i className="fas fa-calendar-plus mr-2"></i>
                  Allocate Monthly
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleResetYearly}
                  disabled={actionLoading}
                  style={{ padding: '8px 16px', fontSize: '14px', backgroundColor: '#3b82f6', borderColor: '#3b82f6' }}
                >
                  <i className="fas fa-sync-alt mr-2"></i>
                  Reset Yearly
                </button>
              </div>
            )}
          </div>

          {/* Leave Table Card */}
          <div className="card employee-content-card">
            <div className="card-body">
              <div className="employee-table-container">
                {/* Search and Filters */}
                <div className="employee-search-container" style={{ marginBottom: '20px' }}>
                  <div className="form-group" style={{ marginBottom: '15px' }}>
                    <input
                      type="text"
                      className="form-input employee-search-input"
                      placeholder="Search by reason..."
                      value={search}
                      onChange={handleSearch}
                    />
                  </div>
                  <div className="row" style={{ marginTop: '10px' }}>
                    <div className="col-md-3">
                      <label htmlFor="employeeFilter" className="form-label" style={{ marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                        <i className="fas fa-info-circle mr-1" style={{ fontSize: '11px', color: '#6b7280' }}></i>
                        Select Employee
                      </label>
                      <select
                        id="employeeFilter"
                        className="form-control"
                        value={filters.employeeId}
                        onChange={(e) => handleFilterChange('employeeId', e.target.value)}
                        style={{ padding: '6px 10px', fontSize: '14px', height: '36px' }}
                      >
                        <option value="all">All Employees</option>
                        {employees.map((emp) => {
                          const empId = emp._id || emp.id;
                          // Prioritize name field (from API response)
                          let empName = emp.name;
                          
                          // Fallback to FirstName + LastName if name doesn't exist
                          if (!empName && emp.FirstName && emp.LastName) {
                            empName = `${emp.FirstName} ${emp.LastName}`.trim();
                          }
                          
                          // Fallback to firstName + lastName if still no name
                          if (!empName && emp.firstName && emp.lastName) {
                            empName = `${emp.firstName} ${emp.lastName}`.trim();
                          }
                          
                          // Final fallback to email or 'Unknown'
                          if (!empName) {
                            empName = emp.email || 'Unknown';
                          }
                          
                          return (
                            <option key={empId} value={empId}>
                              {empName}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="statusFilter" className="form-label" style={{ marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                        Status
                      </label>
                      <select
                        id="statusFilter"
                        className="form-control"
                        value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        style={{ padding: '6px 10px', fontSize: '14px', height: '36px' }}
                      >
                        <option value="">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="leaveTypeFilter" className="form-label" style={{ marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                        Leave Type
                      </label>
                      <select
                        id="leaveTypeFilter"
                        className="form-control"
                        value={filters.leaveType}
                        onChange={(e) => handleFilterChange('leaveType', e.target.value)}
                        style={{ padding: '6px 10px', fontSize: '14px', height: '36px' }}
                      >
                        <option value="">All Leave Types</option>
                        <option value="casual">Casual</option>
                        <option value="sick">Sick</option>
                        <option value="earned">Earned</option>
                        <option value="lop">LOP</option>
                        <option value="permission">Permission</option>
                        <option value="Travel">Travel</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="yearFilter" className="form-label" style={{ marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                        Year
                      </label>
                      <input
                        id="yearFilter"
                        type="number"
                        className="form-control"
                        placeholder="Year"
                        value={filters.year}
                        onChange={(e) => handleFilterChange('year', e.target.value)}
                        style={{ padding: '6px 10px', fontSize: '14px', height: '36px' }}
                      />
                    </div>
                  </div>
                </div>

                {loading ? (
                  <Loading size={120} />
                ) : (
                  <>
                    <div className="employee-table-wrapper">
                      <table className="employee-table">
                        <thead>
                          <tr>
                            <th className="employee-sortable">S.No</th>
                            <th
                              className="employee-sortable"
                              onClick={() => handleSort('employee.name')}
                            >
                              Employee Name
                              {sortBy === 'employee.name' && (
                                <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ml-1`}></i>
                              )}
                              {sortBy !== 'employee.name' && (
                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                              )}
                            </th>
                            <th
                              className="employee-sortable"
                              onClick={() => handleSort('leaveType')}
                            >
                              Leave Type
                              {sortBy === 'leaveType' && (
                                <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ml-1`}></i>
                              )}
                              {sortBy !== 'leaveType' && (
                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                              )}
                            </th>
                            <th
                              className="employee-sortable"
                              onClick={() => handleSort('startDate')}
                            >
                              Start Date
                              {sortBy === 'startDate' && (
                                <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ml-1`}></i>
                              )}
                              {sortBy !== 'startDate' && (
                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                              )}
                            </th>
                            <th
                              className="employee-sortable"
                              onClick={() => handleSort('endDate')}
                            >
                              End Date
                              {sortBy === 'endDate' && (
                                <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ml-1`}></i>
                              )}
                              {sortBy !== 'endDate' && (
                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                              )}
                            </th>
                            <th>Days</th>
                            <th style={{ whiteSpace: 'nowrap', minWidth: '120px' }}>Leave Balance</th>
                            <th
                              className="employee-sortable"
                              onClick={() => handleSort('status')}
                            >
                              Status
                              {sortBy === 'status' && (
                                <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ml-1`}></i>
                              )}
                              {sortBy !== 'status' && (
                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                              )}
                            </th>
                            <th>Reason</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {leaves.length === 0 ? (
                            <tr>
                              <td colSpan="10" className="employee-empty">
                                No leaves found
                              </td>
                            </tr>
                          ) : (
                            leaves.map((leave, index) => {
                              return (
                                <tr key={leave.id}>
                                  <td>{getSerialNumber(index)}</td>
                                  <td>{leave.employee?.name || '-'}</td>
                                  <td>{getLeaveTypeLabel(leave.leaveType)}</td>
                                  <td>{formatDate(leave.startDate)}</td>
                                  <td>{formatDate(leave.endDate)}</td>
                                  <td>{leave.numberOfDays || '-'}</td>
                                  <td className="leave-balance-compact">
                                    {leave.employee?.leaveBalance ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        {leave.employee.leaveBalance.casual && (
                                          <div style={{ whiteSpace: 'nowrap' }}>
                                            <span style={{ fontWeight: 600, color: '#111827' }}>C:</span>
                                            <span style={{ marginLeft: '4px', color: '#111827' }}>
                                              {leave.employee.leaveBalance.casual.balance}
                                            </span>
                                            <span style={{ marginLeft: '4px', color: '#6b7280', fontSize: '11px' }}>
                                              ({leave.employee.leaveBalance.casual.used}/{leave.employee.leaveBalance.casual.allocated})
                                            </span>
                                          </div>
                                        )}
                                        {leave.employee.leaveBalance.sick && (
                                          <div style={{ whiteSpace: 'nowrap' }}>
                                            <span style={{ fontWeight: 600, color: '#111827' }}>S:</span>
                                            <span style={{ marginLeft: '4px', color: '#111827' }}>
                                              {leave.employee.leaveBalance.sick.balance}
                                            </span>
                                            <span style={{ marginLeft: '4px', color: '#6b7280', fontSize: '11px' }}>
                                              ({leave.employee.leaveBalance.sick.used}/{leave.employee.leaveBalance.sick.allocated})
                                            </span>
                                          </div>
                                        )}
                                        {leave.employee.leaveBalance.earned && (
                                          <div style={{ whiteSpace: 'nowrap' }}>
                                            <span style={{ fontWeight: 600, color: '#111827' }}>E:</span>
                                            <span style={{ marginLeft: '4px', color: '#111827' }}>
                                              {leave.employee.leaveBalance.earned.balance}
                                            </span>
                                            <span style={{ marginLeft: '4px', color: '#6b7280', fontSize: '11px' }}>
                                              ({leave.employee.leaveBalance.earned.used}/{leave.employee.leaveBalance.earned.allocated})
                                            </span>
                                          </div>
                                        )}
                                        {leave.employee.leaveBalance.permission && (
                                          <div style={{ whiteSpace: 'nowrap' }}>
                                            <span style={{ fontWeight: 600, color: '#111827' }}>P:</span>
                                            <span style={{ marginLeft: '4px', color: '#111827' }}>
                                              {leave.employee.leaveBalance.permission.balance}
                                            </span>
                                            <span style={{ marginLeft: '4px', color: '#6b7280', fontSize: '11px' }}>
                                              ({leave.employee.leaveBalance.permission.used}/{leave.employee.leaveBalance.permission.allocated})
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      '-'
                                    )}
                                  </td>
                                  <td>{getStatusBadge(leave.status)}</td>
                                  <td style={{ maxWidth: '200px', position: 'relative' }}>
                                    <div style={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      gap: '8px',
                                      overflow: 'hidden'
                                    }}>
                                      <span 
                                        style={{ 
                                          overflow: 'hidden', 
                                          textOverflow: 'ellipsis', 
                                          whiteSpace: 'nowrap',
                                          flex: 1,
                                          cursor: leave.reason && leave.reason.length > 30 ? 'pointer' : 'default'
                                        }}
                                        title={leave.reason || '-'}
                                        onClick={() => leave.reason && leave.reason.length > 30 && handleViewReason(leave.reason, leave.employee?.name)}
                                      >
                                        {truncateText(leave.reason, 30) || '-'}
                                      </span>
                                      {leave.reason && leave.reason.length > 30 && (
                                        <button
                                          type="button"
                                          className="employee-action-btn employee-action-view"
                                          onClick={() => handleViewReason(leave.reason, leave.employee?.name)}
                                          title="View full reason"
                                          style={{ 
                                            padding: '2px 6px',
                                            minWidth: 'auto',
                                            fontSize: '12px',
                                            flexShrink: 0
                                          }}
                                        >
                                          <i className="fa-solid fa-eye"></i>
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    {leave.status === 'pending' && (
                                      <div className="employee-actions">
                                        <button
                                          className="employee-action-btn employee-action-edit"
                                          onClick={() => handleApprove(leave)}
                                          title="Approve"
                                        >
                                          <i className="fa-solid fa-check"></i>
                                        </button>
                                        <button
                                          className="employee-action-btn employee-action-delete"
                                          onClick={() => handleReject(leave)}
                                          title="Reject"
                                        >
                                          <i className="fa-solid fa-times"></i>
                                        </button>
                                      </div>
                                    )}
                                    {leave.status !== 'pending' && (
                                      <span style={{ color: '#6b7280', fontSize: '12px' }}>-</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    {leaves.length > 0 && pagination.totalPages > 0 && (
                      <div className="employee-pagination">
                        <div className="pagination-limit-container" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: 'auto' }}>
                          <label htmlFor="leavePaginationLimit" style={{ margin: 0, fontSize: '14px', color: '#495057' }}>
                            Show:
                          </label>
                          <select
                            id="leavePaginationLimit"
                            value={pagination.limit}
                            onChange={handleLimitChange}
                            style={{
                              padding: '6px 10px',
                              fontSize: '14px',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              backgroundColor: 'white',
                              cursor: 'pointer',
                              minWidth: '70px'
                            }}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="75">75</option>
                            <option value="100">100</option>
                          </select>
                        </div>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(1)}
                            disabled={pagination.currentPage === 1}
                          >
                            «
                          </button>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            disabled={pagination.currentPage === 1}
                          >
                            ‹
                          </button>
                          {getPaginationItems().map((item, index) => {
                            if (item === 'ellipsis') {
                              return (
                                <span
                                  key={`ellipsis-${index}`}
                                  className="pagination-btn"
                                  style={{
                                    cursor: 'default',
                                    padding: '6px 10px',
                                    backgroundColor: 'transparent',
                                    borderColor: 'transparent'
                                  }}
                                >
                                  ...
                                </span>
                              );
                            }

                            return (
                              <button
                                key={item}
                                className={`pagination-btn ${pagination.currentPage === item ? 'active' : ''}`}
                                onClick={() => handlePageChange(item)}
                              >
                                {item}
                              </button>
                            );
                          })}
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            disabled={pagination.currentPage === pagination.totalPages}
                          >
                            ›
                          </button>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(pagination.totalPages)}
                            disabled={pagination.currentPage === pagination.totalPages}
                          >
                            »
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Approve Confirmation Modal */}
      {showApproveModal && (
        <div className="modal-overlay" onClick={closeApproveModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Approve Leave</h3>
              <button className="modal-close" onClick={closeApproveModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to approve leave for <strong>{selectedLeave?.employee?.name}</strong>?</p>
              <p style={{ marginTop: '10px', fontSize: '14px', color: '#6b7280' }}>
                <strong>Leave Type:</strong> {getLeaveTypeLabel(selectedLeave?.leaveType)}<br />
                <strong>Duration:</strong> {selectedLeave?.numberOfDays} day(s)<br />
                <strong>From:</strong> {formatDate(selectedLeave?.startDate)} <strong>To:</strong> {formatDate(selectedLeave?.endDate)}
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeApproveModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmApprove}
                disabled={loading}
                style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
              >
                {loading ? 'Approving...' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Confirmation Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={closeRejectModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reject Leave</h3>
              <button className="modal-close" onClick={closeRejectModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to reject leave for <strong>{selectedLeave?.employee?.name}</strong>?</p>
              <div className="form-group" style={{ marginTop: '15px' }}>
                <label className="form-label">Rejection Reason (Optional)</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter reason for rejection..."
                  style={{ padding: '6px 10px', fontSize: '14px' }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeRejectModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmReject}
                disabled={loading}
                style={{ backgroundColor: '#ef4444', borderColor: '#ef4444' }}
              >
                {loading ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Yearly Leaves Confirmation Modal */}
      {showResetModal && (
        <div className="modal-overlay" onClick={closeResetModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reset Yearly Leaves</h3>
              <button className="modal-close" onClick={closeResetModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p>This will reset casual and sick leaves to 1, and carry forward earned leave balance for the selected year.</p>
              <div className="form-group" style={{ marginTop: '15px' }}>
                <label className="form-label">Year</label>
                <input
                  type="number"
                  className="form-control"
                  value={resetYear}
                  onChange={(e) => setResetYear(parseInt(e.target.value) || new Date().getFullYear())}
                  style={{ padding: '6px 10px', fontSize: '14px', height: '36px' }}
                />
              </div>
              <p style={{ marginTop: '10px', fontSize: '14px', color: '#6b7280' }}>
                <strong>Note:</strong> This action will reset leaves for all employees. Casual and Sick leaves will be reset to 1, and Earned leave balance will be carried forward.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeResetModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmResetYearly}
                disabled={actionLoading}
                style={{ backgroundColor: '#3b82f6', borderColor: '#3b82f6' }}
              >
                {actionLoading ? 'Resetting...' : 'Reset Yearly Leaves'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Allocate Monthly Leaves Confirmation Modal */}
      {showAllocateModal && (
        <div className="modal-overlay" onClick={closeAllocateModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Allocate Monthly Leaves</h3>
              <button className="modal-close" onClick={closeAllocateModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p>This will allocate 1 casual, 1 sick, and 1 earned leave to all active employees for the current month.</p>
              <p style={{ marginTop: '10px', fontSize: '14px', color: '#6b7280' }}>
                <strong>Note:</strong> This action will only allocate if not already allocated for the current month. Only employees with status "active" or "waiting for approval" will receive leaves.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeAllocateModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmAllocateMonthly}
                disabled={actionLoading}
                style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
              >
                {actionLoading ? 'Allocating...' : 'Allocate Monthly Leaves'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Reason Modal */}
      {showReasonModal && (
        <div className="modal-overlay" onClick={closeReasonModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Leave Reason</h3>
              <button className="modal-close" onClick={closeReasonModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '10px', fontSize: '14px', color: '#6b7280' }}>
                <strong>Employee:</strong> {selectedEmployeeName}
              </p>
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#f9fafb', 
                borderRadius: '6px',
                border: '1px solid #e5e7eb',
                minHeight: '60px',
                maxHeight: '300px',
                overflowY: 'auto',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                fontSize: '14px',
                lineHeight: '1.6',
                color: '#374151'
              }}>
                {selectedReason}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeReasonModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveManagement;

